import React from 'react';

import styled from '@emotion/styled';

import { FontWeight, SERIF_FONT_FAMILY, Text } from '@clutter/clean';

import { orderURL } from '@portal/config/routes';
import { LongDistanceMoveFragment, OrderStatusEnum, OrderSubtypeEnum } from '@portal/schema';
import { dateDetailsMap, hasMatchingArrivalTimeWindows } from '@portal/utils/scheduling';

import { LineItem, TrackedLinkAction } from '../../line_item';
import { AppointmentDetailsOrder } from '../full_panel';

const BoldDate = styled.div`
  font-family: ${SERIF_FONT_FAMILY};
  font-weight: 600;
  font-size: 24px;
  line-height: 150%;
`;

const rescheduleButtonTrackingParams = (orderID: string) => ({
  pageName: 'portal:appointment_details',
  container: 'appointment_details',
  action: 'click',
  objectType: 'button',
  objectName: 'reschedule_appointment',
  label: 'Edit',
  value: orderURL(orderID, 'reschedule'),
  order_id: orderID,
});

export const MultiDayTimeline: React.FC<{
  timelineDetailsMap: Map<string, { formattedArrivalWindow: string; serviceDescription: string }>;
  showArrivalTimes: boolean;
  showServiceDescription?: boolean;
  sizing?: 'small' | 'regular' | 'large';
  boldDate?: boolean;
}> = ({ timelineDetailsMap, showArrivalTimes, showServiceDescription = true, sizing = 'regular' }) => {
  const lastEntryIndex = timelineDetailsMap.size - 1;

  return (
    <>
      {Array.from(timelineDetailsMap).map(
        ([formattedDate, { formattedArrivalWindow, serviceDescription }], entryIndex) => (
          <div key={entryIndex}>
            <div>
              {sizing === 'large' && (
                <>
                  <BoldDate>{formattedDate}</BoldDate>
                  {showArrivalTimes && <Text.Callout>{formattedArrivalWindow}</Text.Callout>}
                  {showServiceDescription && <Text.Callout>{serviceDescription}</Text.Callout>}
                </>
              )}
              {sizing === 'regular' && (
                <>
                  <Text.Body weight={FontWeight.Regular}>{formattedDate}</Text.Body>
                  {showArrivalTimes && <Text.Callout>{formattedArrivalWindow}</Text.Callout>}
                  {showServiceDescription && <Text.Callout>{serviceDescription}</Text.Callout>}
                </>
              )}{' '}
              {sizing === 'small' && (
                <>
                  <Text.Callout weight={FontWeight.Regular}>{formattedDate}</Text.Callout>
                  {showArrivalTimes && <Text.Caption>{formattedArrivalWindow}</Text.Caption>}
                  {showServiceDescription && <Text.Caption>{serviceDescription}</Text.Caption>}
                </>
              )}
            </div>
            {entryIndex !== lastEntryIndex && <br />}
          </div>
        ),
      )}
    </>
  );
};

export const MultiDayTimelineLineItem: React.FC<{
  order:
    | AppointmentDetailsOrder
    | LongDistanceMoveFragment['pickup']
    | NonNullable<LongDistanceMoveFragment['delivery']>;
  showServiceDescription?: boolean;
  alwaysShowArrivalTimes?: boolean;
  showEdit?: boolean;
  showNotScheduled?: boolean;
}> = ({
  order,
  showServiceDescription = true,
  alwaysShowArrivalTimes = false,
  showEdit = false,
  showNotScheduled = false,
}) => {
  const nonTravelSourceAndSuccessors = order.nonTravelSourceAndSuccessors.filter(
    (o) => o.subtype !== OrderSubtypeEnum.Travel,
  );
  const timelineDetailsMap = dateDetailsMap(nonTravelSourceAndSuccessors);
  const showArrivalTimes = alwaysShowArrivalTimes || !hasMatchingArrivalTimeWindows(nonTravelSourceAndSuccessors);

  return (
    <LineItem
      label={order.multiDay ? 'Timeline' : 'Date & Time'}
      action={
        showEdit && (
          <TrackedLinkAction
            params={{
              text: 'Edit',
              url: orderURL(order.id, 'reschedule'),
              trackingParams: rescheduleButtonTrackingParams(order.id),
            }}
          />
        )
      }
    >
      {showNotScheduled ? (
        <Text.Body>Not Scheduled</Text.Body>
      ) : (
        <MultiDayTimeline
          timelineDetailsMap={timelineDetailsMap}
          showArrivalTimes={showArrivalTimes}
          showServiceDescription={showServiceDescription}
        />
      )}
    </LineItem>
  );
};
